import {
  FileInfoDto,
  FileType,
  GDTMessageGroup,
  HL7DocumentType,
  HL7MessageGroup,
  QueryFactory,
} from '@lmt/shared-types';

import { showSelectOptionsModal } from '@lmt/unicorn-shared/app/components/common/Dialog/Dialog';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { isEmpty } from '@lmt/unicorn-shared/app/components/merge-assistant/form/components/common';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useEventCallback } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { toast } from 'react-toastify';
import styles from '@lmt/unicorn-shared/src/app/components/patients/profile/cases/components/documents/DocumentItem.module.scss';
import { getDocumentExtension, getDocumentTitle } from './common';
import { useTranslation } from 'react-i18next';
import { TableDotMenu } from '@lmt/unicorn-shared/app/components/common/table/TableDotMenu';

interface DocumentOptionButtonProps {
  document: FileInfoDto;
}

export const DocumentOptionButton: React.FC<DocumentOptionButtonProps> = (
  props,
) => {
  const { document: patientDocument } = props;
  const i18next = useScopedTranslation('patients.documents');
  const i18nextGlobal = useTranslation();
  const i18nextNotifications = useScopedTranslation('patients.notifications');
  const modals = useModal();
  const tabs = useTabs();
  const forms = useGlobalForms();
  const queryClient = useQueryClient();
  const claims = useClaims();
  const { data: gdtSettings } = QueryFactory.GDTSettingsQuery.useGetQuery({
    enabled: claims.permissions.gdt,
  });
  const { data: versionHistory } =
    QueryFactory.FileQuery.useGetVersionHistoryQuery(patientDocument.groupId!, {
      enabled: !!patientDocument.groupId,
    });
  const wasSentViaHL7 = useMemo(() => {
    return (versionHistory ?? []).some((file) => !!file.values?.['HL7WasSent']);
  }, [versionHistory]);
  const { data: hl7OutgoingSettings } =
    QueryFactory.HL7OutgoingSettingsQuery.useGetQuery({
      enabled: claims.permissions.hl7,
    });
  const { data: patient } = QueryFactory.PatientQuery.useGetPatientQuery(
    patientDocument.patientId!,
    {
      enabled: !!patientDocument.patientId,
    },
  );
  const { data: hl7Templates } =
    QueryFactory.HL7OutgoingTemplatesQuery.useGetQuery({
      enabled: claims.permissions.hl7,
    });
  const { data: hl7TemplateRules } =
    QueryFactory.HL7TemplateRulesQuery.useGetQuery(
      {
        subtenantId: patient?.subtenantId,
        isActive: true,
        messageGroup: HL7MessageGroup.Document,
      },
      {
        enabled: claims.permissions.hl7 && !!patient?.subtenantId,
      },
    );

  const hl7TemplateOptions = useMemo(() => {
    const hl7TemplateIds = (hl7TemplateRules ?? [])
      .filter((x) =>
        (wasSentViaHL7
          ? [HL7DocumentType.MDM_T09, HL7DocumentType.MDM_T10]
          : [HL7DocumentType.MDM_T01, HL7DocumentType.MDM_T02]
        ).includes(x.hL7DocumentType!),
      )
      .map((rule) => rule.documentTemplateId);
    return (hl7Templates ?? [])
      .filter((x) => hl7TemplateIds.includes(x.id))
      .map((x) => {
        return {
          id: x.id,
          value: x.title,
        };
      });
  }, [hl7TemplateRules, hl7Templates, wasSentViaHL7]);

  const isHL7Enabled: boolean = useMemo(() => {
    if (
      !claims.permissions.hl7 ||
      (!hl7OutgoingSettings?.enabled &&
        !hl7OutgoingSettings?.outgoingTransporterEnabled)
    ) {
      return false;
    }
    // check if document is .pdf (we can send only .pdf files via HL7)
    const extension = getDocumentExtension(patientDocument.name).toLowerCase();
    if (extension !== '.pdf') {
      return false;
    }
    return hl7TemplateOptions.length > 0;
  }, [
    hl7OutgoingSettings,
    hl7TemplateOptions,
    wasSentViaHL7,
    patientDocument.name,
  ]);

  const { data: gdtTemplates } =
    QueryFactory.GDTOutgoingTemplatesQuery.useGetQuery({
      enabled: claims.permissions.gdt,
    });
  const { data: gdtTemplateRules } =
    QueryFactory.GDTTemplateRulesQuery.useGetQuery(
      {
        subtenantId: patient?.subtenantId,
        isActive: true,
        messageGroup: GDTMessageGroup.Document,
      },
      {
        enabled: claims.permissions.gdt && !!patient?.subtenantId,
      },
    );

  const gdtTemplateOptions = useMemo(() => {
    const gdtTemplateIds = (gdtTemplateRules ?? []).map(
      (rule) => rule.documentTemplateId,
    );
    return (gdtTemplates ?? [])
      .filter((x) => gdtTemplateIds.includes(x.id))
      .map((x) => {
        return {
          id: x.id,
          value: i18nextGlobal.t(x.title as any),
        };
      });
  }, [gdtTemplateRules, gdtTemplates]);

  const isGDTEnabled: boolean = useMemo(() => {
    if (!claims.permissions.gdt || !gdtSettings?.enabled) {
      return false;
    }
    // check if document is .pdf (we can send only .pdf files via GDT)
    const extension = getDocumentExtension(patientDocument.name).toLowerCase();
    if (extension !== '.pdf') {
      return false;
    }
    return gdtTemplateOptions.length > 0;
  }, [gdtSettings, gdtTemplateOptions, patientDocument.name]);

  const documentMailSettingsEnabled =
    QueryFactory.DocumentMailSettingsQuery.useGetIsSendingEnabledQuery(
      patient?.subtenantId!,
      {
        enabled: !!patient?.subtenantId,
      },
    );
  const isEmailSendingEnable: boolean = useMemo(() => {
    if (
      !claims.permissions.sendDocumentByEmail ||
      !documentMailSettingsEnabled?.data
    ) {
      return false;
    }

    const extension = getDocumentExtension(patientDocument.name).toLowerCase();
    if (extension !== '.pdf') {
      return false;
    }

    return true;
  }, [documentMailSettingsEnabled, patientDocument.name]);

  const isDocumentPrintEnable: boolean = useMemo(() => {
    const extension = getDocumentExtension(patientDocument.name).toLowerCase();
    if (extension !== '.pdf') {
      return false;
    }

    return true;
  }, [patientDocument.name]);

  const invalidateDocumentQueries = useEventCallback(
    async (isDeleted?: boolean) => {
      if (isDeleted) {
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.FileQuery.getFileInfoQueryKey(
            patientDocument.id,
          ),
        });
      }
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
          dataBlockId: patientDocument.dataBlockId,
        }),
      });
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
          patientId: patientDocument.patientId,
        }),
      });
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.FileQuery.getVersionHistoryQueryKey(
          patientDocument.groupId,
        ),
      });
    },
  );

  const removeDocument = useEventCallback(async () => {
    const isConfirmed = await modals.showConfirm({
      title: i18next.t('delete_confirmation_title'),
      text: i18next.t('delete_confirmation_text'),
      okButtonText: i18next.t('delete_confirmation_ok_button'),
    });
    if (isConfirmed) {
      await QueryFactory.FileClient.deleteFile(patientDocument.groupId);
    }
    await invalidateDocumentQueries(true);
  });

  const removeLastVersion = useEventCallback(async () => {
    const isConfirmed = await modals.showConfirm({
      title: i18next.t('delete_last_version_confirmation_title'),
      text: i18next.t('delete_last_version_confirmation_text'),
      okButtonText: i18next.t('delete_last_version_confirmation_ok_button'),
    });
    try {
      if (isConfirmed) {
        await QueryFactory.FileClient.deleteVersion(patientDocument.id);
      }
      await invalidateDocumentQueries(true);
    } catch (error) {
      await modals.showError({ text: convertToErrorString(error) });
    }
  });

  const renameDocument = useEventCallback(async () => {
    const newName = await modals.showPrompt({
      defaultValue: getDocumentTitle(patientDocument.name),
      text: i18next.t('rename_prompt_text'),
      title: i18next.t('rename_prompt_title'),
    });

    const ext = getDocumentExtension(patientDocument.name);
    if (newName) {
      await QueryFactory.FileClient.patchFile(patientDocument.id, {
        name: newName?.trim() + ext,
      });
    }
    await invalidateDocumentQueries();
  });

  const printPdf = useEventCallback(async () => {
    // create iframe element
    const iframe = document.createElement('iframe');
    iframe.hidden = true;

    const file = await QueryFactory.FileClient.downloadFile(patientDocument.id);

    // create object URL for your blob or file and set it as the iframe's src
    iframe.src = window.URL.createObjectURL(
      new Blob([file.data], { type: 'application/pdf' }),
    );

    // call the print method in the iframe onload handler
    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    };
    document.body.appendChild(iframe);
  });

  const sendViaGDT = useEventCallback(async () => {
    const toastId = createId();
    let templateIds = gdtTemplateOptions.map((x) => x.id);
    if (gdtTemplateOptions.length > 1) {
      const result = await showSelectOptionsModal(modals, {
        title: i18next.t('select_gdt_templates_title'),
        text: i18next.t('select_gdt_templates_text'),
        okButtonText: i18next.t('select_gdt_templates_ok_text'),
        options: gdtTemplateOptions,
        selectedOptions: gdtTemplateOptions,
      });
      if (isEmpty(result)) {
        return;
      }
      templateIds = result!.map((x) => x.id);
    }
    try {
      toast(i18nextNotifications.t('gdt_message_creating'), {
        type: 'default',
        autoClose: false,
        toastId: toastId,
      });
      await QueryFactory.GDTOutgoingMessageClient.sendDocumentMessage(
        patientDocument.id,
        {
          templateIds,
        },
      );
      toast.update(toastId, {
        render: i18nextNotifications.t('gdt_message_created'),
        type: 'success',
        autoClose: 4000,
      });
      await invalidateDocumentQueries();
    } catch (error) {
      toast.update(toastId, {
        autoClose: 100,
      });
      await modals.showError({ text: convertToErrorString(error) });
    }
  });

  const sendViaHL7 = useEventCallback(async () => {
    const toastId = createId();
    let templateIds = hl7TemplateOptions.map((x) => x.id);
    if (hl7TemplateOptions.length > 1) {
      const result = await showSelectOptionsModal(modals, {
        title: i18next.t('select_hl7_templates_title'),
        text: i18next.t('select_hl7_templates_text'),
        okButtonText: i18next.t('select_hl7_templates_ok_text'),
        options: hl7TemplateOptions,
        selectedOptions: hl7TemplateOptions,
      });
      if (isEmpty(result)) {
        return;
      }
      templateIds = result!.map((x) => x.id);
    }
    try {
      toast(i18nextNotifications.t('hl7_message_creating'), {
        type: 'default',
        autoClose: false,
        toastId: toastId,
      });
      await QueryFactory.HL7OutgoingMessageClient.sendDocumentMessage(
        patientDocument.id,
        {
          templateIds,
        },
      );
      toast.update(toastId, {
        render: i18nextNotifications.t('hl7_message_created'),
        type: 'success',
        autoClose: 4000,
      });
      await invalidateDocumentQueries();
    } catch (error) {
      toast.update(toastId, {
        autoClose: 100,
      });
      await modals.showError({ text: convertToErrorString(error) });
    }
  });

  const sendViaEmail = useEventCallback(async () => {
    const toastId = createId();
    try {
      toast(i18nextNotifications.t('sending_email_started'), {
        type: 'default',
        autoClose: false,
        toastId: toastId,
      });
      await QueryFactory.FileClient.sendFileByEmail(
        patientDocument.id,
        patient?.subtenantId,
      );
      toast.update(toastId, {
        render: i18nextNotifications.t('email_message_was_sent'),
        type: 'success',
        autoClose: 4000,
      });
    } catch (error: any) {
      toast.update(toastId, {
        autoClose: 100,
      });
      await modals.showError({
        text:
          error?.response?.innerMessageException || convertToErrorString(error),
      });
    }
  });

  const openVersionHistory = useEventCallback(() =>
    forms.openFileVersionHistory(
      patientDocument.groupId,
      false,
      undefined,
      undefined,
    ),
  );

  const openDraftInEditor = useEventCallback(() => {
    if (!patientDocument.draftId) return;
    tabs.openTab(
      pages.createPatientDocument.link(
        {
          patientId: patientDocument.patientId!,
        },
        {
          caseId: patientDocument.caseId,
          dataBlockId: patientDocument.dataBlockId,
          visitId: patientDocument.visitId ?? undefined,
          documentId: patientDocument.draftId,
        },
      ),
    );
  });

  const options = useMemo(() => {
    let options = [
      {
        key: 'see_version_history',
        title: i18next.t('option_buttons.see_version_history'),
        onClick: openVersionHistory,
        isHidden: false,
      },
      {
        key: 'rename',
        title: i18next.t('option_buttons.rename'),
        onClick: renameDocument,
        isHidden:
          !claims.permissions.createOrEditDocuments ||
          patientDocument.type === FileType.Document ||
          (patientDocument.type === FileType.DocumentDraft &&
            patientDocument.versionNumber !== 1),
      },
      {
        key: 'send_via_gdt',
        title: i18next.t('option_buttons.send_via_gdt'),
        onClick: sendViaGDT,
        isHidden: !isGDTEnabled,
      },
      {
        key: 'send_via_hl_7',
        title: i18next.t('option_buttons.send_via_hl7'),
        onClick: sendViaHL7,
        isHidden: !isHL7Enabled,
      },
      {
        key: 'send_via_email',
        title: i18next.t('option_buttons.send_via_email'),
        onClick: sendViaEmail,
        isHidden:
          !isEmailSendingEnable || !patient?.email || !patient?.emailsAllowed,
      },
      {
        key: 'print_pdf',
        title: i18next.t('option_buttons.print_pdf'),
        onClick: printPdf,
        isHidden: !isDocumentPrintEnable,
      },
      {
        key: 'delete_last_version',
        title: i18next.t('option_buttons.delete_last_version'),
        onClick: removeLastVersion,
        isHidden:
          !claims.permissions.deleteDocuments ||
          patientDocument.type !== FileType.Document,
      },
      {
        key: 'delete',
        title: i18next.t('option_buttons.delete'),
        onClick: removeDocument,
        isHidden: !claims.permissions.deleteDocuments,
        isDeleteItem: true,
        isUnderLine: true,
      },
    ];

    if (
      claims.permissions.createOrEditDocuments &&
      (patientDocument.type === FileType.Document ||
        patientDocument.type === FileType.DocumentDraft)
    ) {
      options = [
        {
          key: 'open_draft_in_editor',
          title: i18next.t('option_buttons.open_draft_in_editor'),
          onClick: openDraftInEditor,
          isHidden: false,
        },
        ...options,
      ];
    }
    return options;
  }, [
    patientDocument.type,
    claims.permissions,
    patientDocument.draftId,
    isGDTEnabled,
    isHL7Enabled,
    isEmailSendingEnable,
    hl7TemplateOptions,
    patient?.email,
    patient?.emailsAllowed,
    patientDocument.id,
  ]);

  return (
    <TableDotMenu
      testId="document-option-button"
      options={options.filter((x) => x)}
      iconClassName={styles.optionIcon}
    />
  );
};
